/* eslint-disable camelcase */
// --- Request Processors

import { globalState, parseJSON } from '@/components/helpers/utils'
import { getVariantPrices, toVariantId } from '../../storefront/products'
import { recordMarketPrices } from '@/components/helpers/market-price'
import type {
  AthosResult,
  Facet,
  NormalisedFacet,
  Sorting,
  NormalisedSorting,
  Swatch,
  NormalisedVariant,
  NormalisedImage,
  NormalisedResult,
  NormalisedBlogResult
} from 'libs/athos/athos.types'

export function normaliseFacets(facets: Facet[]): NormalisedFacet[] {
  return facets.map((facet) => {
    return {
      ...facet,
      values: facet.values.filter((item) => item.value !== '"')
    }
  })
}

/**
 * Normalise the sort options from the athos API
 */
export function normaliseSortOptions(options: Sorting[]): NormalisedSorting[] {
  return options.map((option) => {
    return {
      ...option,
      active: option.active ?? 0
    }
  })
}

/**
 * Normalise the sibling swatches metafield from the athos API
 */
export function normaliseSwatches(result: any): Swatch[] {
  const parsedSwatches = parseJSON(result?.mfield_dotapparel21_siblings as string, 'array')
  if (!parsedSwatches) return []
  const publishedSwatches = parsedSwatches.filter((swatch: Swatch) => swatch.published)

  const swatchesWithImages = publishedSwatches.map((swatch: Swatch) => {
    return {
      ...swatch,
      url: result.url
    }
  })
  return swatchesWithImages.sort((swatchA: Swatch, swatchB: Swatch) => {
    if (swatchA.isCurrentProduct === swatchB.isCurrentProduct) return 0
    if (swatchA.isCurrentProduct) return -1
    return 1
  })
}

/**
 * Adapt the Athos Variant-First `variants` object into the flat variant shape the
 * result cards already expect (`variant.id`, `variant.price`, `variant.compare_at_price`,
 * plus option values). This is the lowest-risk migration path: instead of refactoring
 * every consumer to read `variants.data[].mappings.core`, we map Athos → legacy shape
 * once, here.
 *
 * Athos native variant entry shape (per migration guide):
 *   { mappings: { core: { uid, sku, name, price, msrp, url, imageUrl, available } },
 *     options: { size: { value }, color: { value } },
 *     attributes: {...}, optionConfig: {...}, badges: [...] }
 *
 * NOTE: Fields the QuickAdd size selector relies on (`option1`/`option2`/`title`,
 * `inventory_quantity`, `mfield_utopia_*`) are NOT part of the documented native
 * variant object — they must be exposed via the AMC feed (surfaced here through the
 * `attributes`/`core` spread). Validate QuickAdd against a live Athos response during
 * pilot QA and extend the mapping below if the feed names differ.
 *
 * @returns legacy-shaped variants, or null when not an Athos variants object
 */
export function adaptAthosVariants(result: AthosResult): NormalisedVariant[] | null {
  const variants = result?.variants
  const variantData = typeof variants === 'object' && variants !== null ? variants.data : undefined
  if (!Array.isArray(variantData)) return null

  return variantData.map((entry: any) => {
    const core = entry?.mappings?.core ?? {}
    const options = entry?.options ?? {}

    // Flatten `{ size: { value: '11' } }` → `{ size: '11' }` for convenient access,
    // while keeping the raw `options`/`optionConfig` for advanced consumers.
    const flatOptions: Record<string, unknown> = {}
    for (const [key, value] of Object.entries<any>(options)) {
      flatOptions[key] = value?.value ?? value
    }

    return {
      ...entry.attributes,
      ...core,
      ...flatOptions,
      id: core.uid ?? entry.uid,
      price: core.price ?? entry.attributes?.price ?? result.price,
      compare_at_price: core.msrp ?? entry.attributes?.compare_at_price ?? result.compare_at_price ?? result.msrp,
      available: core.available,
      options,
      optionConfig: entry.optionConfig,
      badges: entry.badges
    }
  })
}

/**
 * Synthesise a single product-level variant from a result's top-level fields.
 * Used when the API response carries no variant object at all (the case for the
 * current Athos feed, which returns flat variant rows without a `variants` object).
 * Keeps result cards and Quick Add functional.
 *
 * NOTE: `price`/`compare_at_price` are only populated if the AMC feed maps them
 * onto the result. If price display or size selection is empty on the grid, the
 * feed's core-field mapping (price, msrp, per-variant options) needs configuring
 * in the Athos Management Console — the frontend can only surface what is returned.
 */
function synthesiseVariant(result: any): NormalisedVariant {
  const hasAvailability = result.available === true || result.available === false ||
    result.available === 'true' || result.available === 'false'

  return {
    ...result,
    id: result.uid ?? result.id,
    price: result.price,
    compare_at_price: result.compare_at_price ?? result.msrp,
    available: hasAvailability ? (result.available === true || result.available === 'true') : undefined,
    title: result.name
  }
}

/**
 * Adapt the `ss_sizes` AMC feed field into the normalised variants shape.
 * Discovery responses (`/v1/search`, `/v1/category`) carry no `variants`
 * object at all, so grid Quick Add otherwise has no sizes to offer. `ss_sizes`
 * is an HTML-entity-encoded JSON string:
 *   [{"label":"6","variant_id":"44812386107415","available":1}, ...]
 *
 * Not filterable/facetable — `ss_sizes` is an escaped JSON blob and its sibling
 * `ss_instock_sizes` a pipe-joined string; both exist purely for this path.
 */
function adaptSizesFromFeed(result: any): NormalisedVariant[] | null {
  const sizes = parseJSON(result?.ss_sizes as string, 'array')
  if (!Array.isArray(sizes) || !sizes.length) return null

  return sizes.map((size: any) => ({
    id: size.variant_id,
    title: size.label,
    option1: size.label,
    available: size.available === undefined ? undefined : Boolean(Number(size.available))
  }))
}

/**
 * Resolve the variants array for a result regardless of API version / feed shape:
 * - Athos native `variants: { data: [...] }` object → adapt it.
 * - Legacy Searchspring `variants` JSON feed string → parse it.
 * - `ss_sizes` feed field (discovery responses carry no `variants` object) → adapt it.
 * - No variant data at all → synthesise a single product-level variant so the
 *   card can still render.
 */
function resolveVariants(result: any): NormalisedVariant[] {
  const athosVariants = adaptAthosVariants(result)
  if (athosVariants && athosVariants.length) return athosVariants

  const legacyVariants = parseJSON(result.variants, 'array')
  if (Array.isArray(legacyVariants) && legacyVariants.length) return legacyVariants

  const feedSizes = adaptSizesFromFeed(result)
  if (feedSizes && feedSizes.length) return feedSizes

  return [synthesiseVariant(result)]
}

/**
 * Resolve the display images for a result regardless of API version / feed shape:
 * - Legacy Searchspring: delimited `images` / `images_alt` strings.
 * - Athos: discrete `imageUrl` / `parentImageUrl` / `thumbnailImageUrl` fields.
 * Returns an array of `{ src, alt }` compatible with the result cards.
 */
function resolveImages(result: any): NormalisedImage[] {
  if (result.images || result.images_alt) {
    return normaliseImages(result) ?? []
  }

  const srcs = [result.imageUrl, result.parentImageUrl].filter(Boolean)
  const uniqueSrcs = [...new Set(srcs)]
  return uniqueSrcs.map((src) => ({ src, alt: result.name ?? '' }))
}

/**
 * Normalise delimited string/array types into arrays
 */
export function normalisedArray(result: any): any {
  if (result?.includes('|')) {
    return result?.split('|')
  } else if (typeof result === 'string') {
    return [result]
  } else {
    return result
  }
}

/**
 * Normalise the delimited image fields from the athos API
 */
export function normaliseImages(result: any): NormalisedImage[] {
  const { images, images_alt } = result
  const srcs = normalisedArray(images)
  const alts = normalisedArray(images_alt)

  return srcs?.map((src: string, index: number) => {
    return {
      src,
      alt: alts ? alts[index] : ''
    }
  })
}

/**
 * Normalise the product results from the athos API
 */
export async function normaliseResults(results?: AthosResult[] | null): Promise<NormalisedResult[]> {
  if (!results) return []
  const normalisedResults = results.map((result) => {
    const swatches = normaliseSwatches(result)
    const images = resolveImages(result)
    const variants = resolveVariants(result)
    return {
      ...result,
      // `ResultImage` only uses the `images` array for its hover swap (3+ images);
      // otherwise it falls back to `image`. Populate `image` so a single Athos
      // `imageUrl` still renders.
      image: result.image ?? result.imageUrl,
      images,
      swatches,
      variants
    }
  })

  if (!normalisedResults || !normalisedResults.length) return []

  if (requiresPriceLocalisation()) {
    return await localiseResults(normalisedResults)
  }
  return normalisedResults
}

/**
 * Whether feed prices need a Storefront lookup to match the active market.
 *
 * A different presentment currency always needs one. So does a market that shares
 * the shop's currency but can still carry its own price list or adjustment — only
 * the shop's own country is guaranteed to match the base prices the Athos feed was
 * generated from, so that is the one case we can skip the request for.
 */
export function requiresPriceLocalisation(): boolean {
  const { localization, shopify } = globalState
  if (!localization?.country) return false
  if (localization.currency !== shopify.currency) return true

  return Boolean(shopify.country) && localization.country !== shopify.country
}

/**
 * Every variant id a result carries. Athos is variant-first — the top-level `uid`
 * is a variant id (`parentId`/`groupId` hold the product id) — so it is included
 * alongside the normalised variants to cover feeds that expose only the row id.
 */
function collectVariantIds(result: any): Array<string | number> {
  const variantIds = (result.variants ?? [])
    .map((variant: NormalisedVariant) => variant.id)
    .filter(Boolean)

  return result.uid ? [...variantIds, result.uid] : variantIds
}

/**
 * Replace the feed's base-currency prices with the current market's prices.
 * `compare_at_price` is overwritten from the same lookup: leaving the feed value
 * in place would compare a localised price against a base-currency compare-at and
 * fabricate sale states.
 */
async function localiseResults(normalisedResults: any[]): Promise<any[]> {
  const variantIds = normalisedResults.flatMap(collectVariantIds)
  const localisedPrices = await getVariantPrices(variantIds)
  if (!localisedPrices) return normalisedResults

  const pricePairs: Array<[number, number]> = []

  const localisedResults = normalisedResults.map((result) => {
    let localised = false

    const resultVariants = (result.variants ?? []).map((variant: NormalisedVariant, index: number) => {
      // The first variant is the one the cards price, so it also accepts the
      // result-level `uid` match for feeds whose variant rows carry no own id.
      const priceMatch = localisedPrices.get(toVariantId(variant.id)) ??
        (index === 0 ? localisedPrices.get(toVariantId(result.uid)) : undefined)

      if (!priceMatch) return variant
      localised = true

      if (index === 0) {
        pricePairs.push([Number(variant.price), Number(priceMatch.price)])
      }

      return {
        ...variant,
        price: priceMatch.price,
        compare_at_price: priceMatch.compareAtPrice ?? undefined
      }
    })

    if (!localised) return result

    const [firstVariant] = resultVariants

    return {
      ...result,
      variants: resultVariants,
      price: firstVariant.price,
      compare_at_price: firstVariant.compare_at_price
    }
  })

  recordMarketPrices(pricePairs)

  return localisedResults
}

/**
 * Normalise blog article results from the Athos API
 */
export async function normaliseBlogResults(results?: AthosResult[] | null): Promise<NormalisedBlogResult[]> {
  if (!results) return []
  return results.map((result: any) => ({
    id: result.uid,
    title: result.name,
    url: result.url,
    excerpt: result.description,
    image: result.thumbnailImageUrl ? { src: result.thumbnailImageUrl, alt: result.name } : null,
    published_at: result.published_at,
    author: result.author,
    tags: result.tags,
    blog_handle: result.blog_handle
  }))
}

/**
 * Normalise recommendation results from the athos P13N API
 */
export async function normaliseRecommendations(results: any): Promise<NormalisedResult[]> {
  const responseId = results?.responseId
  const normalisedResults = results?.results?.map((result: any) => {
    // Flatten the recommend result: `mappings.core` + `attributes` are hoisted to
    // the top level so the result matches the shape the cards expect.
    const merged = {
      ...result,
      ...result.attributes,
      ...result.mappings?.core
    }

    const swatches = normaliseSwatches(result.attributes)
    const variants = resolveVariants(merged)
    const images = resolveImages(merged)

    return {
      ...merged,
      responseId,
      image: merged.image ?? merged.imageUrl,
      images,
      variants,
      swatches
    }
  })

  if (!normalisedResults || !normalisedResults.length) return []

  if (requiresPriceLocalisation()) {
    return await localiseResults(normalisedResults)
  }

  return normalisedResults
}
